import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum EItemFilter {
    TOP = 'Top',
    BOTTOM = 'Bottom',
    DRESS = 'Dress',
    ABAYS = 'Abays',
    SHOES = 'Shoes',
    ACCESSORIES = 'Acessories',
}

export enum EColorFilter {
    WHITE = 'White',
    BLACK = 'Black',
    RED = 'Red',
    ORANGE = 'Orange',
    BLUE = 'Blue',
    GREEN = 'Green',
}

export interface IFilterItem<T> {
    title: string;
    value: T;
    isActive: boolean;
    isShow?: boolean;
}

export enum ESortType {
    DATE_ADDED = 'Newly Added',
    DESCENDING_PRICE = 'Price (High To Low)',
    ASCENDING_PRICE = 'Price (Low to High)',
}

export type IFilterState = {
    itemFilterList: IFilterItem<EItemFilter>[];
    colorFilterList: IFilterItem<EColorFilter>[];
    currentSortType: ESortType;
    currentMaxPrice: number | null;
    currentMinPrice: number | null;
    genderFilterList: IFilterItem<string>[];
    triggerUpdateSearch: boolean;
    styleMaxPrice: number;
    styleMinPrice: number;
    searchTerm: string;
    searchTermChanged: boolean;
    showFilter: boolean;
    categoryFilterList: IFilterItem<string>[];
    categorySearchTerm: string;
    bodyShapeFilterList: IFilterItem<string>[];
    bodyTypeFilterList: IFilterItem<string>[];
    purposeDefault: IFilterItem<string>;
    brandFilterList: IFilterItem<string>[];
};

export const initialState: any = {
    itemFilterList: Object.keys(EItemFilter).map((filterItem) => ({
        title: EItemFilter[filterItem],
        value: EItemFilter[filterItem],
        isActive: false,
    })),
    colorFilterList: Object.keys(EColorFilter).map((filterItem) => ({
        title: EColorFilter[filterItem],
        value: EColorFilter[filterItem],
        isActive: false,
    })),
    currentSortType: ESortType.DATE_ADDED,
    currentMaxPrice: null,
    currentMinPrice: null,
    genderFilterList: [],
    triggerUpdateSearch: false,
    styleMaxPrice: 0,
    styleMinPrice: 0,
    searchTerm: null,
    searchTermChanged: true,
    showFilter: false,
    categoryFilterList: [],
    categorySearchTerm: '',
    bodyShapeFilterList: [],
    bodyTypeFilterList: [],
    purposeDefault: null,
    isShowCategoryDefault: true,
    brandFilterList: [],
};

const filterSlice = createSlice({
    name: 'filter',
    initialState,
    reducers: {
        checkItemFilter: (state, action: PayloadAction<IFilterItem<EItemFilter | EColorFilter>>) => {
            const index = state.itemFilterList.findIndex((item) => item.value === action.payload.value);
            state.itemFilterList[index].isActive = !state.itemFilterList[index].isActive;
        },
        checkColorFilter: (state, action: PayloadAction<IFilterItem<EColorFilter>>) => {
            const index = state.colorFilterList.findIndex((item) => item.value === action.payload.value);
            state.colorFilterList[index].isActive = !state.colorFilterList[index].isActive;
        },
        setGenderFilter: (state, action: PayloadAction<IFilterItem<string>[]>) => {
            state.genderFilterList = action.payload;
        },
        setExpertiseFilter: (state, action: PayloadAction<IFilterItem<string>[]>) => {
            state.expertiseFilterList = action.payload;
        },
        setLevelFilter: (state, action: PayloadAction<IFilterItem<string>[]>) => {
            state.levelFilterList = action.payload;
        },
        checkGenderFilter: (state, action: PayloadAction<IFilterItem<string>>) => {
            const index = state.genderFilterList.findIndex((item) => item.value === action.payload.value);
            state.genderFilterList[index].isActive = !state.genderFilterList[index].isActive;
        },
        checkExpertiseFilter: (state, action: PayloadAction<IFilterItem<string>>) => {
            const index = state.expertiseFilterList.findIndex((item) => item.value === action.payload.value);
            state.expertiseFilterList[index].isActive = !state.expertiseFilterList[index].isActive;
        },
        checkLevelFilter: (state, action: PayloadAction<IFilterItem<string>>) => {
            const index = state.levelFilterList.findIndex((item) => item.value === action.payload.value);
            state.levelFilterList[index].isActive = !state.levelFilterList[index].isActive;
        },
        changeSortType: (state, action: PayloadAction<ESortType>) => {
            state.currentSortType = action.payload;
        },
        changeMinPrice: (state, action: PayloadAction<number>) => {
            state.currentMinPrice = action.payload;
        },
        changeMaxPrice: (state, action: PayloadAction<number>) => {
            state.currentMaxPrice = action.payload;
        },
        changeTriggerUpdateSearch: (state, action: PayloadAction<boolean>) => {
            state.triggerUpdateSearch = action.payload;
        },
        resetFilter: (state) => {
            let showFilter;
            if (state.purposeDefault) showFilter = true;
            else showFilter = false;
            // reset all filter values but keep style min price and max price
            return {
                ...initialState,
                showFilter: showFilter,
                purposeDefault: state.purposeDefault,
                styleMinPrice: state.styleMinPrice,
                styleMaxPrice: state.styleMaxPrice,
            };
        },
        setIsCategoryDefault: (state, action) => {
            state.isShowCategoryDefault = action.payload;
        },
        setStyleMinMaxPrice: (state, action) => {
            state.styleMinPrice = action.payload.min;
            state.styleMaxPrice = action.payload.max;
        },
        setSearchTerm: (state, action) => {
            state.searchTerm = action.payload;
        },
        setSearchTermChanged: (state, action) => {
            state.searchTermChanged = action.payload;
        },
        setShowFilter: (state, action) => {
            state.showFilter = action.payload;
        },
        setCategoryFilter: (state, action: PayloadAction<IFilterItem<string>[]>) => {
            state.categoryFilterList = action.payload;
        },
        setPurposeFilter: (state, action: PayloadAction<IFilterItem<string> | null>) => {
            state.purposeDefault = action.payload;
        },
        checkCategoryFilter: (state, action: PayloadAction<IFilterItem<string>>) => {
            const index = state.categoryFilterList.findIndex((item) => item.value === action.payload.value);
            state.categoryFilterList[index].isActive = !state.categoryFilterList[index].isActive;
        },
        searchCategoryFilter: (state, action) => {
            state.categoryFilterList = state.categoryFilterList.map((i) => {
                i.isShow = i.value.toLowerCase().includes(action.payload.toLowerCase());
                return i;
            });
        },
        setCategorySearchTerm: (state, action) => {
            state.categorySearchTerm = action.payload;
        },
        setBodyShapeFilter: (state, action: PayloadAction<IFilterItem<string>[]>) => {
            state.bodyShapeFilterList = action.payload;
        },
        checkBodyShapeFilter: (state, action: PayloadAction<IFilterItem<string>>) => {
            const index = state.bodyShapeFilterList.findIndex((item) => item.value === action.payload.value);
            state.bodyShapeFilterList[index].isActive = !state.bodyShapeFilterList[index].isActive;
        },
        setBodyTypeFilter: (state, action: PayloadAction<IFilterItem<string>[]>) => {
            state.bodyTypeFilterList = action.payload;
        },
        checkBodyTypeFilter: (state, action: PayloadAction<IFilterItem<string>>) => {
            const index = state.bodyTypeFilterList.findIndex((item) => item.value === action.payload.value);
            state.bodyTypeFilterList[index].isActive = !state.bodyTypeFilterList[index].isActive;
        },
        setBrandsFilter: (state, action: PayloadAction<IFilterItem<string>[]>) => {
            state.brandFilterList = action.payload;
        },
        checkBrandsFilter: (state, action: PayloadAction<IFilterItem<string>>) => {
            const index = state.brandFilterList.findIndex((item) => item.value === action.payload.value);
            state.brandFilterList[index].isActive = !state.brandFilterList[index].isActive;
        },
    },
});

export const {
    checkItemFilter,
    checkColorFilter,
    changeSortType,
    changeMinPrice,
    changeMaxPrice,
    checkGenderFilter,
    checkExpertiseFilter,
    checkLevelFilter,
    changeTriggerUpdateSearch,
    resetFilter,
    setGenderFilter,
    setExpertiseFilter,
    setLevelFilter,
    setStyleMinMaxPrice,
    setSearchTerm,
    setSearchTermChanged,
    setShowFilter,
    setCategoryFilter,
    checkCategoryFilter,
    searchCategoryFilter,
    setCategorySearchTerm,
    setBodyShapeFilter,
    checkBodyShapeFilter,
    setBodyTypeFilter,
    checkBodyTypeFilter,
    setPurposeFilter,
    setIsCategoryDefault,
    setBrandsFilter,
    checkBrandsFilter,
    // searchBrandsFilter
} = filterSlice.actions;

export const filterReducer = filterSlice.reducer;
